import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import Sidebar from "@/components/Sidebar";
import BottomNav from "@/components/BottomNav";
import Logo from "@/components/Logo";
import Capture from "@/pages/Capture";
import Inbox from "@/pages/Inbox";
import Ahead from "@/pages/Ahead";
import Timeline from "@/pages/Timeline";
import Settings from "@/pages/Settings";
import AccountHousehold from "@/pages/AccountHousehold";
import Auth from "@/pages/Auth";
import PartnerJoinSheet from "@/components/PartnerJoinSheet";
import JoinInviteSheet from "@/components/JoinInviteSheet";
import { MAINTENANCE_MODE } from "@/config/maintenance";

function Shell() {
  const { user, ready, bootError, retry } = useAuth();

  // Manual deploy-window maintenance state. Inert when off; never clears tokens.
  if (MAINTENANCE_MODE) {
    return (
      <div
        data-testid="maintenance-screen"
        className="min-h-screen flex flex-col items-center justify-center gap-5 bg-[var(--bg)] px-6 text-center"
      >
        <Logo />
        <p className="max-w-sm text-[var(--ink)] text-base">
          We’re updating Heldall. Please try again in a moment.
        </p>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
        <Loader2 className="animate-spin text-[var(--pine)]" size={28} />
      </div>
    );
  }

  if (bootError) {
    return (
      <div
        data-testid="boot-error-screen"
        className="min-h-screen flex flex-col items-center justify-center gap-5 bg-[var(--bg)] px-6 text-center"
      >
        <Logo />
        <p className="max-w-sm text-[var(--ink)] text-base">
          We couldn’t reach Heldall just now. We might be updating — your account and
          everything you’ve captured are safe. Give it a moment, then try again.
        </p>
        <button
          data-testid="boot-error-retry-button"
          onClick={retry}
          className="rounded-full bg-[var(--pine)] px-6 py-2.5 text-white text-sm font-medium transition-opacity hover:opacity-90"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!user) return <Auth />;

  return (
    <BrowserRouter>
      <div className="md:flex md:flex-row">
        <Sidebar />
        <div className="flex-1 min-w-0 flex flex-col">
          <header
            className="md:hidden sticky top-0 z-30 app-header-safe bg-[var(--nav-bg)] backdrop-blur-md border-b border-[var(--line)]"
            data-testid="mobile-header"
          >
            <div className="h-[54px] px-5 flex items-center">
              <Link to="/" data-testid="mobile-logo-link" aria-label="Go to Capture">
                <Logo size={24} />
              </Link>
            </div>
          </header>
          <main className="flex-1 min-w-0 pb-bottom-nav">
            <Routes>
              <Route path="/" element={<Capture />} />
              <Route path="/inbox" element={<Inbox />} />
              <Route path="/ahead" element={<Ahead />} />
              <Route path="/timeline" element={<Timeline />} />
              <Route path="/activity" element={<Timeline />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/account" element={<AccountHousehold />} />
              <Route path="/join" element={<Capture />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </div>
        <BottomNav />
        <PartnerJoinSheet />
        <JoinInviteSheet />
      </div>
    </BrowserRouter>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="min-h-screen paper-grad">
        <AuthProvider>
          <Shell />
          <Toaster
            position="top-center"
            richColors
            offset={28}
            duration={3000}
            toastOptions={{ style: { padding: "14px 20px", fontSize: "15px" } }}
          />
        </AuthProvider>
      </div>
    </ThemeProvider>
  );
}

export default App;
